import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — scroll-triggered animation wrapper.
 *
 * On mount, the element starts hidden (opacity 0 + transform). It transitions
 * to visible once it enters the viewport (or after a 50ms fallback if it's
 * already in view at mount). Each instance can specify a delay for natural
 * staggered animations across siblings.
 */
export default function Reveal({
  children,
  variant = "up",
  delay = 0,
  duration = 700,
  threshold = 0,
  rootMargin = "0px 0px -5% 0px",
  className = "",
  style,
  as: Tag = "div",
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let observer;
    let raf;
    let fallback;

    // Fallback safety: if IO doesn't fire for any reason (some headless envs,
    // hidden parents, etc.), force-visible after a short delay so content
    // never stays hidden permanently.
    fallback = setTimeout(() => setVisible(true), 400);

    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisible(true);
              clearTimeout(fallback);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold, rootMargin },
      );
      // Defer observe to next frame to avoid layout race on first paint
      raf = requestAnimationFrame(() => observer.observe(el));
    } else {
      setVisible(true);
    }

    return () => {
      if (observer) observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (fallback) clearTimeout(fallback);
    };
  }, [threshold, rootMargin]);

  const initial = {
    up:    "translate3d(0, 28px, 0)",
    down:  "translate3d(0, -28px, 0)",
    left:  "translate3d(-36px, 0, 0)",
    right: "translate3d(36px, 0, 0)",
    zoom:  "scale(0.96)",
    fade:  "none",
  }[variant] || "translate3d(0, 28px, 0)";

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : initial,
        transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: visible ? "auto" : "opacity, transform",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
